import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage } from './firebase';

const MAX_IMAGE_SIZE = 500; // Max width/height in pixels
const JPEG_QUALITY = 0.8;

/**
 * Compress and resize an image file
 */
const compressImage = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > MAX_IMAGE_SIZE) {
          height = Math.round((height * MAX_IMAGE_SIZE) / width);
          width = MAX_IMAGE_SIZE;
        }
      } else {
        if (height > MAX_IMAGE_SIZE) {
          width = Math.round((width * MAX_IMAGE_SIZE) / height);
          height = MAX_IMAGE_SIZE;
        }
      }

      canvas.width = width;
      canvas.height = height;

      if (ctx) {
        // Draw white background (for transparent PNGs)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        
        // Draw the image
        ctx.drawImage(img, 0, 0, width, height);
      }

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Upload a profile image and return the download URL
 */
export const uploadProfileImage = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    console.log(`📸 Uploading profile image for ${userId}`);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('Image must be less than 10MB');
    }

    // Compress the image
    const compressedBlob = await compressImage(file);
    console.log(`📦 Compressed image: ${(compressedBlob.size / 1024).toFixed(1)}KB`);

    // Create storage reference
    const storageRef = ref(storage, `profileImages/${userId}/avatar.jpg`);

    // Upload the compressed image
    const snapshot = await uploadBytes(storageRef, compressedBlob, {
      contentType: 'image/jpeg',
      customMetadata: {
        uploadedAt: new Date().toISOString(),
      },
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log(`✅ Profile image uploaded: ${downloadURL}`);

    return downloadURL;
  } catch (error) {
    console.error('❌ Error uploading profile image:', error);
    throw error;
  }
};

/**
 * Delete a user's profile image
 */
export const deleteProfileImage = async (userId: string): Promise<void> => {
  try {
    console.log(`🗑️ Deleting profile image for ${userId}`);
    const storageRef = ref(storage, `profileImages/${userId}/avatar.jpg`);
    await deleteObject(storageRef);
    console.log(`✅ Profile image deleted`);
  } catch (error: any) {
    // Ignore if file doesn't exist
    if (error.code === 'storage/object-not-found') {
      console.log('ℹ️ No profile image to delete');
      return;
    }
    console.error('❌ Error deleting profile image:', error);
    throw error;
  }
};

/**
 * Get initials from a name or username
 */
export const getInitials = (name?: string, username?: string): string => {
  if (name && name.trim()) {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  if (username) {
    return username.substring(0, 2).toUpperCase();
  }
  
  return '??';
};

/**
 * Generate a consistent color based on user ID
 */
export const getAvatarColor = (userId: string): string => {
  const colors = [
    '#E57373', // Red
    '#F06292', // Pink
    '#BA68C8', // Purple
    '#9575CD', // Deep Purple
    '#7986CB', // Indigo
    '#64B5F6', // Blue
    '#4FC3F7', // Light Blue
    '#4DD0E1', // Cyan
    '#4DB6AC', // Teal
    '#81C784', // Green
    '#AED581', // Light Green
    '#DCE775', // Lime
    '#FFD54F', // Amber
    '#FFB74D', // Orange
    '#FF8A65', // Deep Orange
    '#A1887F', // Brown
  ];
  
  // Generate a hash from the userId
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return colors[Math.abs(hash) % colors.length];
};

export default {
  uploadProfileImage,
  deleteProfileImage,
  getInitials,
  getAvatarColor,
};